import React, { useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useSelector, useDispatch } from 'react-redux'
import { isEmpty } from 'lodash'
import {
  Drawer,
  List,
  ListItem,
  ListItemText,
  Box,
  useTheme,
  useMediaQuery,
  IconButton,
  Divider,
  Avatar,
  Typography,
} from '@mui/material'
import {
  ChevronLeft as ChevronLeftIcon,
  Person as PersonIcon,
  Search as SearchIcon,
  Description as DescriptionIcon,
  SwapHoriz as SwapHorizIcon,
  Notifications as NotificationsIcon,
  FilterList as FilterListIcon,
  MenuBook as MenuBookIcon,
  Business as BusinessIcon,
  Home as HomeIcon,
  People as PeopleIcon,
  Work as WorkIcon,
  Group as GroupIcon,
  BusinessCenter as BusinessCenterIcon,
  WorkOutline as WorkOutlineIcon,
  PersonOutline as PersonOutlineIcon,
  Favorite as FavoriteIcon,
  NotificationsActive as NotificationsActiveIcon,
  Menu as MenuIcon,
  Logout as LogoutIcon,
  SupportAgent as SupportAgentIcon,
  MailOutline as MailOutlineIcon,
  AttachMoney as MoneyIcon,
} from '@mui/icons-material'
import { useHandleLogout } from 'components/Layout/Header/helpers'
import Logo from 'static/images/logo.svg'
import { getSuperUser } from 'data/superUser/actions'
import { useIsUser } from 'pages/Identity/Login'
import { fetchGetEmployerUser } from 'src/redux/employerUser'
import { fetchGetCandidate } from 'src/redux/candidate'
import { checkEmpty, imageAddress } from 'data/helpers'

const DRAWER_WIDTH = 240
const COLLAPSED_DRAWER_WIDTH = 85

const NavigationDrawer = () => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const location = useLocation()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const [collapsed, setCollapsed] = useState(false)
  const [mobileOpen, setMobileOpen] = useState(false)

  const identity = useSelector(state => state.identity)
  const { candidateId, employerUserId } = identity
  const candidate = useSelector(state => state.candidate)
  const employerUser = useSelector(state => state.employerUser)
  const superUser = useSelector(state => state.superUser)
  const { userId, isCandidate, isEmployerUser, isSuper } = useIsUser()

  const handleLogout = useHandleLogout(dispatch, navigate)
  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen)
  }

  const handleNavigation = path => {
    navigate(path)
    if (isMobile) {
      setMobileOpen(false)
    }
  }

  const toggleDrawer = () => setCollapsed(!collapsed)

  const getProfilePath = () => {
    if (candidateId) return '/users/candidate/profile'
    if (employerUserId) return 'users/employer_user/profile'
    return '/login'
  }

  const getProfileInfo = () => {
    const user = candidate.id ? candidate : employerUser
    return {
      name: user.id ? `${user.firstName} ${user.lastName}` : 'My Profile',
      image: user.profileImage || '',
      path: getProfilePath(),
    }
  }

  useEffect(() => {
    if (isCandidate && isEmpty(candidate)) {
      dispatch(fetchGetCandidate(candidateId))
    } else if (isEmployerUser && isEmpty(employerUser)) {
      dispatch(fetchGetEmployerUser(employerUserId))
    } else if (isSuper && isEmpty(superUser)) {
      dispatch(getSuperUser(userId))
    }
  }, [
    isCandidate,
    isEmployerUser,
    isSuper,
    candidate,
    employerUser,
    superUser,
    candidateId,
    employerUserId,
    userId,
    dispatch,
  ])

  const profileInfo = getProfileInfo()
  const parsedProfileImage = checkEmpty(profileInfo.image) ? {} : profileInfo.image
  const { key, fileName } = parsedProfileImage
  const imgSrc = key ? imageAddress(key, fileName) : null

  const commonListItemStyles = {
    minHeight: 40,
    justifyContent: collapsed ? 'center' : 'initial',
    '& .MuiTypography-root': {
      fontSize: '0.875rem',
    },
  }

  const commonIconStyles = {
    fontSize: 20,
    mr: collapsed ? 0 : 2,
  }

  const renderCandidateMenuItems = () => (
    <>
      {/* Main Navigation */}
      <ListItem
        button
        onClick={() => handleNavigation('/jobs')}
        selected={location.pathname === '/jobs'}
        sx={{
          ...commonListItemStyles,
          px: 2.5,
          mx: 1,
          backgroundColor:
            location.pathname !== '/jobs' ? `${theme.palette.primary.main}08` : 'transparent',
          borderRadius: 1,
          '&.Mui-selected': {
            backgroundColor: collapsed ? 'transparent' : theme.palette.action.selected,
          },
          '&:hover': {
            backgroundColor:
              location.pathname !== '/jobs'
                ? `${theme.palette.primary.main}12`
                : theme.palette.action.hover,
          },
          '& .MuiTypography-root': {
            ...commonListItemStyles['& .MuiTypography-root'],
            fontWeight: 500,
            color: theme.palette.text.primary,
          },
        }}
      >
        <SearchIcon
          sx={{
            ...commonIconStyles,
            fontSize: 22,
            color: theme.palette.text.primary,
          }}
        />
        {!collapsed && <ListItemText primary='Job Search' />}
      </ListItem>

      {/* Job Management Section */}
      <ListItem
        sx={{
          ...commonListItemStyles,
          px: 2.5,
          mx: 1,
          mt: 2,
          '& .MuiTypography-root': {
            color: theme.palette.text.secondary,
            fontSize: '0.75rem',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
          },
        }}
      >
        {!collapsed && <ListItemText primary='Job Management' />}
      </ListItem>

      <ListItem
        button
        onClick={() => handleNavigation('/applications')}
        selected={location.pathname === '/applications'}
        sx={{
          ...commonListItemStyles,
          pl: collapsed ? 2.5 : 6,
          pr: 2.5,
          '&.Mui-selected': {
            backgroundColor: collapsed ? 'transparent' : theme.palette.action.selected,
          },
          '&:hover': {
            backgroundColor: theme.palette.action.hover,
          },
        }}
      >
        <DescriptionIcon sx={commonIconStyles} />
        {!collapsed && <ListItemText primary='My Job Applications' />}
      </ListItem>

      {/* Community Section */}
      <ListItem
        sx={{
          ...commonListItemStyles,
          px: 2.5,
          mx: 1,
          mt: 2,
          '& .MuiTypography-root': {
            color: theme.palette.text.secondary,
            fontSize: '0.75rem',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
          },
        }}
      >
        {!collapsed && <ListItemText primary='Community' />}
      </ListItem>

      <ListItem
        button
        onClick={() => handleNavigation('/resources')}
        selected={location.pathname === '/resources'}
        sx={{
          ...commonListItemStyles,
          pl: collapsed ? 2.5 : 6,
          pr: 2.5,
          '&.Mui-selected': {
            backgroundColor: collapsed ? 'transparent' : theme.palette.action.selected,
          },
          '&:hover': {
            backgroundColor: theme.palette.action.hover,
          },
        }}
      >
        <MenuBookIcon sx={commonIconStyles} />
        {!collapsed && <ListItemText primary='Resources' />}
      </ListItem>

      <ListItem
        button
        onClick={() => handleNavigation('/directory')}
        selected={location.pathname === '/directory'}
        sx={{
          ...commonListItemStyles,
          pl: collapsed ? 2.5 : 6,
          pr: 2.5,
          '&.Mui-selected': {
            backgroundColor: collapsed ? 'transparent' : theme.palette.action.selected,
          },
          '&:hover': {
            backgroundColor: theme.palette.action.hover,
          },
        }}
      >
        <BusinessIcon sx={commonIconStyles} />
        {!collapsed && <ListItemText primary='Directory' />}
      </ListItem>

      <ListItem
        button
        onClick={() => handleNavigation('/housing')}
        selected={location.pathname === '/housing'}
        sx={{
          ...commonListItemStyles,
          pl: collapsed ? 2.5 : 6,
          pr: 2.5,
          '&.Mui-selected': {
            backgroundColor: collapsed ? 'transparent' : theme.palette.action.selected,
          },
          '&:hover': {
            backgroundColor: theme.palette.action.hover,
          },
        }}
      >
        <HomeIcon sx={commonIconStyles} />
        {!collapsed && <ListItemText primary='Relocation Help' />}
      </ListItem>

      <ListItem
        button
        onClick={() => handleNavigation('/residency-transfer')}
        selected={location.pathname === '/residency-transfer'}
        sx={{
          ...commonListItemStyles,
          pl: collapsed ? 2.5 : 6,
          pr: 2.5,
          '&.Mui-selected': {
            backgroundColor: collapsed ? 'transparent' : theme.palette.action.selected,
          },
          '&:hover': {
            backgroundColor: theme.palette.action.hover,
          },
        }}
      >
        <SwapHorizIcon sx={commonIconStyles} />
        {!collapsed && <ListItemText primary='Residency Transfer' />}
      </ListItem>

      <ListItem
        button
        onClick={() => handleNavigation('/salary')}
        selected={location.pathname === '/salary'}
        sx={{
          ...commonListItemStyles,
          pl: collapsed ? 2.5 : 6,
          pr: 2.5,
          '&.Mui-selected': {
            backgroundColor: collapsed ? 'transparent' : theme.palette.action.selected,
          },
          '&:hover': {
            backgroundColor: theme.palette.action.hover,
          },
        }}
      >
        <MoneyIcon sx={commonIconStyles} />
        {!collapsed && <ListItemText primary='Salary Transparency' />}
      </ListItem>


      {/* Preferences Section */}
      <ListItem
        sx={{
          ...commonListItemStyles,
          px: 2.5,
          mx: 1,
          mt: 2,
          '& .MuiTypography-root': {
            color: theme.palette.text.secondary,
            fontSize: '0.75rem',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
          },
        }}
      >
        {!collapsed && <ListItemText primary='Preferences' />}
      </ListItem>

      <ListItem
        button
        onClick={() => handleNavigation('/users/alerts')}
        selected={location.pathname === '/users/alerts'}
        sx={{
          ...commonListItemStyles,
          pl: collapsed ? 2.5 : 6,
          pr: 2.5,
          '&.Mui-selected': {
            backgroundColor: collapsed ? 'transparent' : theme.palette.action.selected,
          },
          '&:hover': {
            backgroundColor: theme.palette.action.hover,
          },
        }}
      >
        <NotificationsIcon sx={commonIconStyles} />
        {!collapsed && <ListItemText primary='My Alerts' />}
      </ListItem>

      <ListItem
        button
        onClick={() => handleNavigation('/users/filters')}
        selected={location.pathname === '/users/filters'}
        sx={{
          ...commonListItemStyles,
          pl: collapsed ? 2.5 : 6,
          pr: 2.5,
          '&.Mui-selected': {
            backgroundColor: collapsed ? 'transparent' : theme.palette.action.selected,
          },
          '&:hover': {
            backgroundColor: theme.palette.action.hover,
          },
        }}
      >
        <FilterListIcon sx={commonIconStyles} />
        {!collapsed && <ListItemText primary='Saved Filters' />}
      </ListItem>

      <ListItem
        sx={{
          ...commonListItemStyles,
          px: 2.5,
          mx: 1,
          mt: 2,
          '& .MuiTypography-root': {
            color: theme.palette.text.secondary,
            fontSize: '0.75rem',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
          },
        }}
      >
        {!collapsed && <ListItemText primary='Help' />}
      </ListItem>
      <ListItem
        button
        onClick={() => handleNavigation('/support')}
        selected={location.pathname === '/support'}
        sx={{
          ...commonListItemStyles,
          pl: collapsed ? 2.5 : 6,
          pr: 2.5,
          '&.Mui-selected': {
            backgroundColor: collapsed ? 'transparent' : theme.palette.action.selected,
          },
          '&:hover': {
            backgroundColor: theme.palette.action.hover,
          },
        }}
      >
        <SupportAgentIcon sx={commonIconStyles} />
        <ListItemText primary={!collapsed ? 'Support' : ''} />
      </ListItem>
      <ListItem
        button
        onClick={() => handleNavigation('/contact')}
        selected={location.pathname === '/contact'}
        sx={{
          ...commonListItemStyles,
          pl: collapsed ? 2.5 : 6,
          pr: 2.5,
          '&.Mui-selected': {
            backgroundColor: collapsed ? 'transparent' : theme.palette.action.selected,
          },
          '&:hover': {
            backgroundColor: theme.palette.action.hover,
          },
        }}
      >
        <MailOutlineIcon sx={commonIconStyles} />
        <ListItemText primary={!collapsed ? 'Contact' : ''} />
      </ListItem>
    </>
  )

  const renderEmployerMenuItems = () => {
    const employerName = employerUser?.employerName || 'Hospital'

    return (
      <>
        {/* Main Navigation */}
        <ListItem
          button
          onClick={() => handleNavigation('/physicians')}
          selected={location.pathname === '/physicians'}
          sx={{
            ...commonListItemStyles,
            px: 2.5,
            mx: 1,
            '&.Mui-selected': {
              backgroundColor: collapsed ? 'transparent' : theme.palette.action.selected,
            },
            '&:hover': {
              backgroundColor: theme.palette.action.hover,
            },
          }}
        >
          <PeopleIcon sx={commonIconStyles} />
          {!collapsed && <ListItemText primary='Providers' />}
        </ListItem>

        <ListItem
          button
          onClick={() => handleNavigation('/directory')}
          selected={location.pathname === '/directory'}
          sx={{
            ...commonListItemStyles,
            px: 2.5,
            mx: 1,
            '&.Mui-selected': {
              backgroundColor: collapsed ? 'transparent' : theme.palette.action.selected,
            },
            '&:hover': {
              backgroundColor: theme.palette.action.hover,
            },
          }}
        >
          <BusinessIcon sx={commonIconStyles} />
          {!collapsed && <ListItemText primary='Directory' />}
        </ListItem>

        {/* Management Section */}
        <ListItem
          sx={{
            ...commonListItemStyles,
            px: 2.5,
            mx: 1,
            mt: 2,
            '& .MuiTypography-root': {
              color: theme.palette.text.secondary,
              fontSize: '0.75rem',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
            },
          }}
        >
          {!collapsed && <ListItemText primary='Management' />}
        </ListItem>

        <ListItem
          button
          onClick={() => handleNavigation('/jobs')}
          selected={location.pathname === '/jobs'}
          sx={{
            ...commonListItemStyles,
            pl: collapsed ? 2.5 : 6,
            pr: 2.5,
            '&.Mui-selected': {
              backgroundColor: collapsed ? 'transparent' : theme.palette.action.selected,
            },
            '&:hover': {
              backgroundColor: theme.palette.action.hover,
            },
          }}
        >
          <WorkIcon sx={commonIconStyles} />
          {!collapsed && <ListItemText primary={'My Jobs'} />}
        </ListItem>

        <ListItem
          button
          onClick={() => handleNavigation('/hospitals/profile')}
          selected={location.pathname === '/hospitals/profile'}
          sx={{
            ...commonListItemStyles,
            pl: collapsed ? 2.5 : 6,
            pr: 2.5,
            '&.Mui-selected': {
              backgroundColor: collapsed ? 'transparent' : theme.palette.action.selected,
            },
            '&:hover': {
              backgroundColor: theme.palette.action.hover,
            },
          }}
        >
          <BusinessCenterIcon sx={commonIconStyles} />
          {!collapsed && <ListItemText primary={employerName} />}
        </ListItem>

        <ListItem
          button
          onClick={() => handleNavigation('/admin/users')}
          selected={location.pathname === '/admin/users'}
          sx={{
            ...commonListItemStyles,
            pl: collapsed ? 2.5 : 6,
            pr: 2.5,
            '&.Mui-selected': {
              backgroundColor: collapsed ? 'transparent' : theme.palette.action.selected,
            },
            '&:hover': {
              backgroundColor: theme.palette.action.hover,
            },
          }}
        >
          <GroupIcon sx={commonIconStyles} />
          {!collapsed && <ListItemText primary='Users' />}
        </ListItem>

        {/* Preferences Section */}
        <ListItem
          sx={{
            ...commonListItemStyles,
            px: 2.5,
            mx: 1,
            mt: 2,
            '& .MuiTypography-root': {
              color: theme.palette.text.secondary,
              fontSize: '0.75rem',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
            },
          }}
        >
          {!collapsed && <ListItemText primary='Preferences' />}
        </ListItem>

        <ListItem
          button
          onClick={() => handleNavigation('/users/alerts')}
          selected={location.pathname === '/users/alerts'}
          sx={{
            ...commonListItemStyles,
            pl: collapsed ? 2.5 : 6,
            pr: 2.5,
            '&.Mui-selected': {
              backgroundColor: collapsed ? 'transparent' : theme.palette.action.selected,
            },
            '&:hover': {
              backgroundColor: theme.palette.action.hover,
            },
          }}
        >
          <NotificationsIcon sx={commonIconStyles} />
          {!collapsed && <ListItemText primary='Alerts' />}
        </ListItem>

        <ListItem
          button
          onClick={() => handleNavigation('/users/filters')}
          selected={location.pathname === '/users/filters'}
          sx={{
            ...commonListItemStyles,
            pl: collapsed ? 2.5 : 6,
            pr: 2.5,
            '&.Mui-selected': {
              backgroundColor: collapsed ? 'transparent' : theme.palette.action.selected,
            },
            '&:hover': {
              backgroundColor: theme.palette.action.hover,
            },
          }}
        >
          <FilterListIcon sx={commonIconStyles} />
          {!collapsed && <ListItemText primary='Filters' />}
        </ListItem>

        <ListItem
          sx={{
            ...commonListItemStyles,
            px: 2.5,
            mx: 1,
            mt: 2,
            '& .MuiTypography-root': {
              color: theme.palette.text.secondary,
              fontSize: '0.75rem',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
            },
          }}
        >
          {!collapsed && <ListItemText primary='Help' />}
        </ListItem>
        <ListItem
          button
          onClick={() => handleNavigation('/support')}
          selected={location.pathname === '/support'}
          sx={{
            ...commonListItemStyles,
            pl: collapsed ? 2.5 : 6,
            pr: 2.5,
            '&.Mui-selected': {
              backgroundColor: collapsed ? 'transparent' : theme.palette.action.selected,
            },
            '&:hover': {
              backgroundColor: theme.palette.action.hover,
            },
          }}
        >
          <SupportAgentIcon sx={commonIconStyles} />
          <ListItemText primary={!collapsed ? 'Support' : ''} />
        </ListItem>
        <ListItem
          button
          onClick={() => handleNavigation('/contact')}
          selected={location.pathname === '/contact'}
          sx={{
            ...commonListItemStyles,
            pl: collapsed ? 2.5 : 6,
            pr: 2.5,
            '&.Mui-selected': {
              backgroundColor: collapsed ? 'transparent' : theme.palette.action.selected,
            },
            '&:hover': {
              backgroundColor: theme.palette.action.hover,
            },
          }}
        >
          <MailOutlineIcon sx={commonIconStyles} />
          <ListItemText primary={!collapsed ? 'Contact' : ''} />
        </ListItem>
      </>
    )
  }

  const renderAdminMenuItems = () => (
    <>
      <ListItem
        button
        onClick={() => handleNavigation('/super/employers')}
        selected={location.pathname === '/super/employers'}
        sx={{
          ...commonListItemStyles,
          px: 2.5,
          mx: 1,
          '&.Mui-selected': {
            backgroundColor: collapsed ? 'transparent' : theme.palette.action.selected,
          },
          '&:hover': {
            backgroundColor: theme.palette.action.hover,
          },
        }}
      >
        <BusinessCenterIcon sx={commonIconStyles} />
        {!collapsed && <ListItemText primary='Employers' />}
      </ListItem>

      <ListItem
        button
        onClick={() => handleNavigation('/super/employer_users')}
        selected={location.pathname === '/super/employer_users'}
        sx={{
          ...commonListItemStyles,
          px: 2.5,
          mx: 1,
          '&.Mui-selected': {
            backgroundColor: collapsed ? 'transparent' : theme.palette.action.selected,
          },
          '&:hover': {
            backgroundColor: theme.palette.action.hover,
          },
        }}
      >
        <GroupIcon sx={commonIconStyles} />
        {!collapsed && <ListItemText primary='Employer Users' />}
      </ListItem>

      <ListItem
        button
        onClick={() => handleNavigation('/super/jobs')}
        selected={location.pathname === '/super/jobs'}
        sx={{
          ...commonListItemStyles,
          px: 2.5,
          mx: 1,
          '&.Mui-selected': {
            backgroundColor: collapsed ? 'transparent' : theme.palette.action.selected,
          },
          '&:hover': {
            backgroundColor: theme.palette.action.hover,
          },
        }}
      >
        <WorkOutlineIcon sx={commonIconStyles} />
        {!collapsed && <ListItemText primary='Jobs' />}
      </ListItem>

      <ListItem
        button
        onClick={() => handleNavigation('/super/candidates')}
        selected={location.pathname === '/super/candidates'}
        sx={{
          ...commonListItemStyles,
          px: 2.5,
          mx: 1,
          '&.Mui-selected': {
            backgroundColor: collapsed ? 'transparent' : theme.palette.action.selected,
          },
          '&:hover': {
            backgroundColor: theme.palette.action.hover,
          },
        }}
      >
        <PersonOutlineIcon sx={commonIconStyles} />
        {!collapsed && <ListItemText primary='Candidates' />}
      </ListItem>

      <ListItem
        button
        onClick={() => handleNavigation('/super/partner_interest')}
        selected={location.pathname === '/super/partner_interest'}
        sx={{
          ...commonListItemStyles,
          px: 2.5,
          mx: 1,
          '&.Mui-selected': {
            backgroundColor: collapsed ? 'transparent' : theme.palette.action.selected,
          },
          '&:hover': {
            backgroundColor: theme.palette.action.hover,
          },
        }}
      >
        <FavoriteIcon sx={commonIconStyles} />
        {!collapsed && <ListItemText primary='Partner Interest' />}
      </ListItem>

      <ListItem
        button
        onClick={() => handleNavigation('/super/job_interest')}
        selected={location.pathname === '/super/job_interest'}
        sx={{
          ...commonListItemStyles,
          px: 2.5,
          mx: 1,
          '&.Mui-selected': {
            backgroundColor: collapsed ? 'transparent' : theme.palette.action.selected,
          },
          '&:hover': {
            backgroundColor: theme.palette.action.hover,
          },
        }}
      >
        <NotificationsActiveIcon sx={commonIconStyles} />
        {!collapsed && <ListItemText primary='Job Interest' />}
      </ListItem>

      <ListItem
        button
        onClick={() => handleNavigation('/super/housing')}
        selected={location.pathname === '/super/housing'}
        sx={{
          ...commonListItemStyles,
          px: 2.5,
          mx: 1,
          '&.Mui-selected': {
            backgroundColor: collapsed ? 'transparent' : theme.palette.action.selected,
          },
          '&:hover': {
            backgroundColor: theme.palette.action.hover,
          },
        }}
      >
        <HomeIcon sx={commonIconStyles} />
        {!collapsed && <ListItemText primary='Housing' />}
      </ListItem>
    </>
  )

  const drawerContent = (
    <>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: collapsed ? 'center' : 'flex-start',
          px: collapsed ? 1 : 2.5,
          py: 2,
          borderRadius: 1,
          mx: 1,
          mt: 1,
        }}
      >
        <Box
          component="img"
          src={Logo}
          alt="MyStethi"
          sx={{
            height: 32,
            width: 32,
          }}
        />
        {!collapsed && (
          <Typography
            variant="h6"
            sx={{
              ml: 2,
              color: 'text.primary',
              fontWeight: 600,
              fontSize: '1.25rem'
            }}
          >
            MyStethi
          </Typography>
        )}
      </Box>

      {!collapsed && (
        <Divider
          sx={{
            borderColor: theme.palette.divider,
            borderWidth: '1px',
            opacity: 0.2,
            mx: 1,
            mb: 1,
          }}
        />
      )}

      {!isSuper && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            px: 1,
            pt: 1,
            pb: 2,
            position: 'relative',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              width: '100%',
              position: 'relative',
            }}
          >
            <Box sx={{ flex: 1 }} /> {/* Left spacer */}
            <Avatar
              sx={{
                width: 48,
                height: 48,
                cursor: 'pointer',
                transition: 'transform 0.2s',
                '&:hover': {
                  transform: 'scale(1.05)',
                },
              }}
              onClick={() => handleNavigation(profileInfo.path)}
              src={imgSrc}
            >
              {!imgSrc && <PersonIcon />}
            </Avatar>
            {!isMobile && (
              <Box sx={{ flex: 1, display: 'flex', justifyContent: 'flex-end', pr: 1 }}>
                <IconButton
                  onClick={toggleDrawer}
                  sx={{
                    padding: 0.5,
                    '&:hover': {
                      backgroundColor: 'transparent',
                    },
                  }}
                >
                  <ChevronLeftIcon
                    sx={{
                      transform: collapsed ? 'rotate(180deg)' : 'none',
                      transition: theme.transitions.create('transform', {
                        duration: theme.transitions.duration.shortest,
                      }),
                      fontSize: 24,
                      color: theme.palette.text.primary,
                    }}
                  />
                </IconButton>
              </Box>
            )}
          </Box>
          {!collapsed && (
            <ListItemText
              primary={profileInfo.name}
              secondary='View Profile'
              sx={{
                cursor: 'pointer',
                textAlign: 'center',
                mt: 1,
                '& .MuiTypography-root': {
                  fontSize: '0.875rem',
                },
                '& .MuiTypography-body1': {
                  fontSize: '1.25rem',
                  fontWeight: 500,
                },
                '& .MuiTypography-body2': {
                  fontSize: '0.75rem',
                  color: theme.palette.primary.dark,
                },
              }}
              onClick={() => handleNavigation(profileInfo.path)}
            />
          )}
        </Box>
      )}

      {!collapsed && (
        <Divider
          sx={{
            borderColor: theme.palette.divider,
            borderWidth: '1px',
            opacity: 0.2,
          }}
        />
      )}

      <Box sx={{ overflow: 'auto' }}>
        <List>
          {isSuper
            ? renderAdminMenuItems()
            : isEmployerUser
              ? renderEmployerMenuItems()
              : renderCandidateMenuItems()}
        </List>
      </Box>

      <Box sx={{ mt: 'auto' }}>
        <Divider
          sx={{
            borderColor: theme.palette.divider,
            borderWidth: '1px',
            opacity: 0.2,
          }}
        />
        <ListItem
          button
          onClick={handleLogout}
          sx={{
            ...commonListItemStyles,
            px: 2.5,
            mx: 1,
            mt: 1,
            '&:hover': {
              backgroundColor: theme.palette.action.hover,
            },
          }}
        >
          <Typography
            sx={{
              color: theme.palette.error.main,
              fontWeight: 500,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <LogoutIcon sx={{ fontSize: 20 }} />
            {!collapsed && 'Logout'}
          </Typography>
        </ListItem>
      </Box>
    </>
  )

  return (
    <>
      {/* Mobile Top Bar with Menu Button */}
      {isMobile && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            height: '56px',
            backgroundColor: theme.palette.background.paper,
            borderBottom: '1px solid',
            borderColor: 'divider',
            display: 'flex',
            alignItems: 'center',
            padding: '0 16px',
            zIndex: theme.zIndex.drawer + 1,
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          }}
        >
          <IconButton
            color="primary"
            aria-label="open navigation"
            onClick={handleDrawerToggle}
            sx={{ mr: 2 }}
          >
            <MenuIcon />
          </IconButton>
          <Box
            component="img"
            src={Logo}
            alt="MyStethi"
            sx={{
              height: 28,
              width: 28,
              cursor: 'pointer',
            }}
            onClick={() => handleNavigation('/')}
          />
          <Typography
            variant="h6"
            sx={{
              ml: 1,
              color: 'text.primary',
              fontWeight: 500,
              fontSize: '1.1rem'
            }}
          >
            MyStethi
          </Typography>
        </Box>
      )}

      <Box
        component='nav'
        sx={{
          width: { sm: collapsed ? COLLAPSED_DRAWER_WIDTH : DRAWER_WIDTH },
          flexShrink: { sm: 0 },
          position: 'fixed',
          top: 0, // Both mobile and desktop start from top now
          height: '100%', // Full height for both mobile and desktop
          zIndex: theme.zIndex.drawer,
        }}
      >
        {/* Mobile drawer */}
        <Drawer
          variant='temporary'
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile
          }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: DRAWER_WIDTH,
              borderRight: '1px solid rgba(0, 0, 0, 0.12)',
              top: isMobile ? '56px' : 0, // Start below the mobile top bar
              height: isMobile ? 'calc(100% - 56px)' : '100%', // Account for top bar height
            },
          }}
        >
          {drawerContent}
        </Drawer>
        {/* Desktop drawer */}
        <Drawer
          variant='permanent'
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': {
              width: collapsed ? COLLAPSED_DRAWER_WIDTH : DRAWER_WIDTH,
              boxSizing: 'border-box',
              borderRight: '1px solid rgba(0, 0, 0, 0.12)',
              transition: theme.transitions.create('width', {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.enteringScreen,
              }),
              overflowX: 'hidden',
              top: 0, // Start from the very top
              height: '100%', // Full height
            },
          }}
          open
        >
          {drawerContent}
        </Drawer>
      </Box>
      {/* Add a spacer to push main content to the right of the drawer */}
      <Box
        sx={{
          display: { xs: 'none', sm: 'block' },
          width: { sm: collapsed ? COLLAPSED_DRAWER_WIDTH : DRAWER_WIDTH },
          flexShrink: 0,
        }}
      />
    </>
  )
}

export default NavigationDrawer
